// src/icons/iconTags.js
//
// Maps each ICON_REGISTRY key to a list of category words and synonyms,
// so searching "transport" surfaces car/train/bus/bike, "money" surfaces
// wallet/cart/piggyBank, etc. Keep tags lowercase; the picker lowercases
// the query too.

export const ICON_TAGS = {
  // --- General / misc ---
  rocket: ['launch', 'startup', 'space', 'boost'],
  graduationCap: ['school', 'education', 'graduation', 'study', 'university'],
  star: ['favorite', 'rating', 'featured'],
  heart: ['love', 'like', 'favorite'],
  flag: ['milestone', 'goal', 'marker', 'country'],
  gift: ['present', 'birthday', 'reward'],
  trophy: ['award', 'winner', 'achievement', 'prize'],
  puzzle: ['plugin', 'piece', 'integration', 'module'],
  gear: ['settings', 'config', 'preferences', 'options'],
  wrench: ['tools', 'repair', 'fix', 'maintenance', 'settings'],
  compass: ['navigation', 'direction', 'explore', 'travel'],
  map: ['navigation', 'location', 'travel', 'geography'],
  pin: ['location', 'marker', 'geo', 'place', 'map'],
  pinMap: ['location', 'marker', 'geo', 'place', 'map'],
  lightbulb: ['idea', 'tip', 'insight', 'light'],
  lightning: ['energy', 'power', 'fast', 'electric', 'bolt'],
  megaphone: ['announcement', 'marketing', 'broadcast', 'promotion'],
  palette: ['design', 'art', 'color', 'creative'],
  brush: ['design', 'art', 'paint', 'creative'],
  pencil: ['edit', 'write', 'draft', 'design'],
  pen: ['edit', 'write', 'draft', 'signature'],
  eraser: ['delete', 'remove', 'clear', 'edit'],
  key: ['security', 'password', 'access', 'unlock'],
  lock: ['security', 'private', 'locked', 'protection'],
  unlock: ['security', 'open', 'unlocked', 'access'],
  shield: ['security', 'protection', 'safety', 'privacy'],
  shieldLock: ['security', 'protection', 'privacy', 'password'],
  eye: ['view', 'visibility', 'watch', 'see'],
  eyeSlash: ['hide', 'invisible', 'privacy'],
  bell: ['notification', 'alert', 'reminder'],
  bellSlash: ['mute', 'notification off', 'silent'],
  clock: ['time', 'schedule', 'duration'],
  stopwatch: ['time', 'timer', 'speed', 'performance'],
  hourglass: ['time', 'waiting', 'loading', 'duration'],
  magnet: ['attract', 'pull', 'tool'],
  fingerprint: ['security', 'identity', 'biometric', 'authentication'],
  infinity: ['unlimited', 'endless', 'loop'],
  crosshair: ['target', 'focus', 'aim', 'precision'],

  // --- Folders / files ---
  folder: ['storage', 'directory', 'files', 'organize'],
  file: ['document', 'storage'],
  fileEarmark: ['document', 'storage'],
  fileText: ['document', 'text', 'notes'],
  fileCode: ['document', 'code', 'programming', 'dev'],
  fileImage: ['document', 'photo', 'picture', 'media'],
  fileMusic: ['document', 'audio', 'sound', 'media'],
  filePdf: ['document', 'pdf'],
  fileWord: ['document', 'word', 'text'],
  fileExcel: ['document', 'spreadsheet', 'excel', 'finance'],
  filePpt: ['document', 'presentation', 'slides'],
  fileLock: ['document', 'security', 'private'],
  fileCheck: ['document', 'approved', 'done', 'validated'],
  fileX: ['document', 'rejected', 'removed'],
  filePlus: ['document', 'add', 'new'],
  filePerson: ['document', 'profile', 'resume', 'cv'],
  clipboard: ['tasks', 'notes', 'checklist'],
  clipboardCheck: ['tasks', 'done', 'checklist', 'approved'],
  clipboardData: ['tasks', 'data', 'report', 'stats'],
  archive: ['storage', 'files', 'backup'],
  box: ['storage', 'package', 'delivery'],
  boxSeam: ['storage', 'package', 'delivery', 'shipping'],
  floppy: ['save', 'storage', 'disk'],
  printer: ['office', 'print', 'document'],

  // --- Calendar / time ---
  calendar: ['schedule', 'date', 'event', 'planner', 'time'],
  calendarCheck: ['schedule', 'confirmed', 'appointment', 'time'],
  calendarEvent: ['schedule', 'appointment', 'meeting', 'time'],
  calendarPlus: ['schedule', 'add event', 'new appointment', 'time'],
  calendarX: ['schedule', 'cancelled', 'removed event', 'time'],
  calendarHeart: ['schedule', 'anniversary', 'special date', 'time'],
  calendarWeek: ['schedule', 'week view', 'planner', 'time'],
  alarm: ['clock', 'reminder', 'time', 'wake up'],

  // --- Communication / social ---
  envelope: ['email', 'mail', 'message', 'communication'],
  envelopeOpen: ['email', 'mail', 'read', 'communication'],
  envelopeCheck: ['email', 'mail', 'sent', 'confirmed', 'communication'],
  envelopeHeart: ['email', 'mail', 'love letter', 'communication'],
  chat: ['message', 'conversation', 'communication', 'talk'],
  chatDots: ['message', 'typing', 'conversation', 'communication'],
  chatText: ['message', 'conversation', 'communication', 'sms'],
  chatHeart: ['message', 'conversation', 'love', 'communication'],
  chatSquare: ['message', 'conversation', 'communication'],
  telephone: ['phone', 'call', 'contact', 'communication'],
  telephonePlus: ['phone', 'call', 'add contact', 'communication'],
  mic: ['audio', 'microphone', 'record', 'voice', 'podcast'],
  micMute: ['audio', 'muted', 'silence', 'voice'],
  send: ['message', 'submit', 'share', 'communication'],
  rss: ['feed', 'subscribe', 'news', 'blog'],
  reply: ['message', 'respond', 'communication'],
  people: ['team', 'group', 'community', 'users'],
  person: ['user', 'profile', 'account', 'contact'],
  personBadge: ['user', 'profile', 'id', 'employee'],
  personCheck: ['user', 'verified', 'approved', 'confirmed'],
  personPlus: ['user', 'add contact', 'new member', 'invite'],
  personX: ['user', 'remove', 'blocked', 'delete contact'],
  personVcard: ['user', 'contact card', 'profile', 'business card'],
  personHeart: ['user', 'favorite person', 'relationship'],

  // --- Shopping / finance ---
  shoppingBag: ['shopping', 'store', 'retail', 'purchase', 'money'],
  bagHeart: ['shopping', 'favorite item', 'wishlist', 'money'],
  cart: ['shopping', 'ecommerce', 'checkout', 'purchase', 'money'],
  cartCheck: ['shopping', 'order confirmed', 'checkout', 'money'],
  cartPlus: ['shopping', 'add to cart', 'checkout', 'money'],
  basket: ['shopping', 'groceries', 'market', 'money'],
  handbag: ['shopping', 'fashion', 'accessory', 'money'],
  wallet: ['money', 'finance', 'payment', 'budget'],
  creditCard: ['money', 'payment', 'finance', 'banking'],
  piggyBank: ['money', 'savings', 'finance', 'budget'],
  safe: ['money', 'security', 'vault', 'finance', 'savings'],
  ticket: ['event', 'pass', 'admission', 'travel', 'money'],
  tag: ['label', 'price', 'discount', 'category', 'money'],
  tags: ['label', 'price', 'discount', 'category', 'money'],
  receipt: ['money', 'invoice', 'payment', 'finance'],
  graph: ['chart', 'stats', 'analytics', 'finance', 'growth'],
  pieChart: ['chart', 'stats', 'analytics', 'finance', 'data'],

  // --- Home / building ---
  house: ['home', 'residence', 'property', 'real estate'],
  houseDoor: ['home', 'residence', 'property', 'real estate'],
  houseHeart: ['home', 'favorite property', 'real estate'],
  houses: ['home', 'neighborhood', 'real estate', 'property'],
  building: ['office', 'company', 'business', 'real estate'],
  buildings: ['office', 'company', 'business', 'city', 'real estate'],
  door: ['entrance', 'home', 'access'],
  lamp: ['light', 'home', 'furniture'],

  // --- Tech / hardware ---
  server: ['tech', 'hosting', 'infrastructure', 'backend', 'data'],
  database: ['tech', 'data', 'storage', 'backend'],
  cpu: ['tech', 'processor', 'hardware', 'computer'],
  phone: ['mobile', 'tech', 'device', 'communication'],
  laptop: ['tech', 'computer', 'device', 'work'],
  tv: ['tech', 'television', 'media', 'entertainment'],
  tablet: ['tech', 'device', 'mobile'],
  hdd: ['tech', 'storage', 'hardware', 'disk'],
  keyboard: ['tech', 'input', 'typing', 'device'],
  plug: ['tech', 'power', 'electric', 'connection'],
  modem: ['tech', 'internet', 'network', 'router'],
  router: ['tech', 'internet', 'network', 'wifi'],
  motherboard: ['tech', 'hardware', 'computer', 'component'],
  sim: ['tech', 'mobile', 'phone', 'network'],
  display: ['tech', 'screen', 'monitor'],
  mouse: ['tech', 'input', 'device', 'computer'],
  webcam: ['tech', 'video', 'camera', 'call'],

  // --- Media / audio ---
  camera: ['photo', 'picture', 'media'],
  cameraReels: ['video', 'film', 'media'],
  cameraVideo: ['video', 'film', 'media', 'call'],
  play: ['media', 'video', 'audio', 'start'],
  playCircle: ['media', 'video', 'audio', 'start'],
  pause: ['media', 'video', 'audio', 'stop'],
  stop: ['media', 'video', 'audio', 'halt'],
  skipForward: ['media', 'next', 'audio', 'video'],
  skipBackward: ['media', 'previous', 'audio', 'video'],
  fastForward: ['media', 'audio', 'video', 'speed'],
  rewind: ['media', 'audio', 'video', 'back'],
  eject: ['media', 'remove', 'disc'],
  record: ['media', 'audio', 'video', 'recording'],
  vinyl: ['music', 'audio', 'record', 'media'],
  musicPlayer: ['music', 'audio', 'media', 'player'],
  boombox: ['music', 'audio', 'media', 'speaker'],
  volumeUp: ['audio', 'sound', 'media'],
  volumeMute: ['audio', 'silent', 'media'],
  speaker: ['audio', 'sound', 'music', 'media'],
  controller: ['gaming', 'games', 'joystick', 'media'],
  book: ['reading', 'education', 'library', 'media'],
  bookmark: ['saved', 'favorite', 'reading'],
  bookmarkHeart: ['saved', 'favorite', 'reading'],
  bookmarks: ['saved', 'favorites', 'reading', 'library'],

  // --- Weather ---
  cloud: ['weather', 'sky', 'overcast'],
  cloudRain: ['weather', 'rain', 'sky'],
  cloudSun: ['weather', 'partly cloudy', 'sky'],
  cloudMoon: ['weather', 'night', 'sky'],
  cloudLightning: ['weather', 'storm', 'thunder', 'sky'],
  cloudSnow: ['weather', 'snow', 'winter', 'sky'],
  clouds: ['weather', 'sky', 'overcast'],
  cloudy: ['weather', 'sky', 'overcast'],
  sun: ['weather', 'sunny', 'day', 'sky'],
  moon: ['weather', 'night', 'sky'],
  moonStars: ['weather', 'night', 'sky'],
  droplet: ['weather', 'water', 'rain', 'liquid'],

  // --- Nature / health ---
  tree: ['nature', 'plant', 'environment', 'outdoors'],
  leaf: ['nature', 'plant', 'eco', 'environment'],
  heartPulse: ['health', 'medical', 'fitness', 'wellness'],
  heartbreak: ['health', 'emotion', 'sad'],
  hospital: ['health', 'medical', 'clinic'],
  lungs: ['health', 'medical', 'breathing'],
  bandaid: ['health', 'medical', 'first aid', 'wound'],
  flask: ['science', 'lab', 'chemistry', 'research'],
  beaker: ['science', 'lab', 'chemistry', 'research'],
  paw: ['animal', 'pet', 'nature'],

  // --- Transport / travel ---
  airplane: ['transport', 'travel', 'flight', 'plane'],
  car: ['transport', 'travel', 'vehicle', 'automobile', 'drive'],
  bus: ['transport', 'travel', 'vehicle', 'public transit'],
  train: ['transport', 'travel', 'vehicle', 'rail', 'public transit'],
  truck: ['transport', 'vehicle', 'delivery', 'shipping'],
  taxi: ['transport', 'travel', 'vehicle', 'ride', 'cab'],
  ev: ['transport', 'vehicle', 'electric', 'car'],
  fuelPump: ['transport', 'gas', 'fuel', 'vehicle'],
  suitcase: ['travel', 'luggage', 'trip', 'vacation'],
  suitcaseLg: ['travel', 'luggage', 'trip', 'vacation'],
  backpack: ['travel', 'bag', 'school', 'hiking'],
  luggage: ['travel', 'bag', 'trip', 'vacation'],
  passport: ['travel', 'identity', 'trip', 'international'],
  globe: ['travel', 'world', 'international', 'geography'],
  globeEurope: ['travel', 'world', 'international', 'geography'],
  globeAsia: ['travel', 'world', 'international', 'geography'],
  bike: ['transport', 'travel', 'vehicle', 'bicycle', 'cycling'],

  // --- Shapes / status ---
  circle: ['shape'],
  square: ['shape'],
  diamond: ['shape'],
  triangle: ['shape'],
  hexagon: ['shape'],
  pentagon: ['shape'],
  octagon: ['shape'],
  checkCircle: ['status', 'success', 'done', 'approved'],
  xCircle: ['status', 'error', 'cancel', 'rejected'],
  exclamationCircle: ['status', 'warning', 'alert'],
  exclamationTriangle: ['status', 'warning', 'alert', 'caution'],
  infoCircle: ['status', 'information', 'help'],
  questionCircle: ['status', 'help', 'faq', 'unknown'],
  plusCircle: ['status', 'add', 'new'],
  dashCircle: ['status', 'remove', 'minus'],
  slashCircle: ['status', 'disabled', 'blocked'],

  // --- Emoji ---
  emojiSmile: ['emotion', 'happy', 'face'],
  emojiHeartEyes: ['emotion', 'love', 'face'],
  emojiLaughing: ['emotion', 'happy', 'face', 'funny'],
  emojiFrown: ['emotion', 'sad', 'face'],
  emojiWink: ['emotion', 'playful', 'face'],
  emojiSunglasses: ['emotion', 'cool', 'face'],
  emojiNeutral: ['emotion', 'face', 'meh'],
  emojiAngry: ['emotion', 'mad', 'face'],
  emojiDizzy: ['emotion', 'confused', 'face'],

  // --- Signs / directions ---
  signStop: ['road', 'traffic', 'sign', 'transport'],
  signYield: ['road', 'traffic', 'sign', 'transport'],
  signTurnLeft: ['road', 'traffic', 'sign', 'direction', 'transport'],
  signTurnRight: ['road', 'traffic', 'sign', 'direction', 'transport'],
  arrowUpCircle: ['direction', 'navigation', 'up'],
  arrowDownCircle: ['direction', 'navigation', 'down'],
  arrowLeftCircle: ['direction', 'navigation', 'left'],
  arrowRightCircle: ['direction', 'navigation', 'right'],
};